import { Injectable } from '@angular/core';
import { PageTranslations } from '../interfaces/page-translations';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, forkJoin } from 'rxjs';
import { TranslationsParts } from '../interfaces/translations';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class TranslatorService {
     langsPerPath: PageTranslations[] = [];
     translations:TranslationsParts[] = [];
     filesLoaded:boolean = false;
     activeLanguage: string = "en";
     currentRoute: string = "";
     hasChanges = new BehaviorSubject<boolean>(false);
     _hasChanges$ = this.hasChanges.asObservable();

     constructor(private http: HttpClient, private router: Router) { 
          router.events.subscribe(event => {
               this.currentRoute = router.url;
               const regex = /\/[\d\w]/;
               if(this.currentRoute.includes("@") || regex.test(this.currentRoute)){
                    const lastDexOfSlash = this.currentRoute.lastIndexOf('/');
                    if(lastDexOfSlash > 0){
                         this.currentRoute = this.currentRoute.substring(0,lastDexOfSlash);
                    }
               }
               this.currentRoute = this.currentRoute.replace("/#","");
          });

          const pathConfigs = this.router.config;
          pathConfigs.forEach((config : any)  => {
               if(config.data && config.data.languages){
                    this.langsPerPath.push({path:'/'+config.path as any, languages:config.data.languages});
               }
          });

          const forceLoadValue = new Date().getTime();
          const url = `${environment.BASE_URL}/assets/content/languages/RootTranslationFile.json?v=` + forceLoadValue;
          const _base = this.http.get(url);
          const url_audit = `${environment.BASE_URL}/assets/content/languages/AuditTranslations.json?v=` + forceLoadValue;
          const _audit = this.http.get(url_audit);
          const url_driver = `${environment.BASE_URL}/assets/content/languages/DriverTranslations.json?v=` + forceLoadValue;
          const _driver = this.http.get(url_driver);
          const url_invoice = `${environment.BASE_URL}/assets/content/languages/InvoiceTranslations.json?v=` + forceLoadValue;
          const _invoice = this.http.get(url_invoice);
          const url_payment = `${environment.BASE_URL}/assets/content/languages/PaymentTranslations.json?v=` + forceLoadValue;
          const _payment = this.http.get(url_payment);
          const url_request = `${environment.BASE_URL}/assets/content/languages/RequestRecordsTranslations.json?v=` + forceLoadValue;
          const _request = this.http.get(url_request);
          const url_customer = `${environment.BASE_URL}/assets/content/languages/CustomerTranslations.json?v=` + forceLoadValue;
          const _customer = this.http.get(url_customer);
          const url_veterans = `${environment.BASE_URL}/assets/content/languages/VeteransTranslations.json?v=` + forceLoadValue;
          const _veterans = this.http.get(url_veterans);
          const url_webservice = `${environment.BASE_URL}/assets/content/languages/WebServiceTranslations.json?v=` + forceLoadValue;
          const _webservice = this.http.get(url_webservice);
          const url_women = `${environment.BASE_URL}/assets/content/languages/WomenTranslations.json?v=` + forceLoadValue;
          const _women = this.http.get(url_women);

          forkJoin([_base,_audit,_driver,_invoice,_payment,_request,_customer,_veterans,_webservice, _women]).subscribe(
               (results: any[]) => {
                    
                    
                    this.translations.push({file:"base",translations:[]});  
                    this.translations.push({file:"audit",translations:[]});  
                    this.translations.push({file:"driver",translations:[]});  
                    this.translations.push({file:"invoice",translations:[]});  
                    this.translations.push({file:"payment",translations:[]});  
                    this.translations.push({file:"request",translations:[]});  
                    this.translations.push({file:"customer",translations:[]});  
                    this.translations.push({file:"veterans",translations:[]});
                    this.translations.push({ file: "webservice", translations: [] });
                    this.translations.push({ file: "women", translations: [] });
                    results.forEach((tFile : any, index:number)  => {
                         tFile.dataToTranslate.forEach((row: any) => {
                              this.translations[index].translations.push({tag:row.tag as any, en:row.lang[0].text, es:row.lang[1].text, fr:row.lang[2].text });  
                         });
                    });
                    
                    this.filesLoaded = true;
               },
               error => {
                    console.error('Error fetching data:', error);
               }
          );
     }

     public get translationsReady(){
          return this.filesLoaded;
     }

     public setActiveLanguage(value:string){
          this.activeLanguage = value.toLocaleLowerCase();
          window.document.documentElement.lang = value.toLocaleLowerCase();
          this.hasChanges.next(true);
     }
     
     public restHasChange(){
          this.hasChanges.next(false);;
     }

     public getActiveLanguage(){
          return this.activeLanguage;
     }

     public backupLanguage() {
          const date = new Date();
          date.setTime(date.getTime() + (1 * 1 * 15 * 60 * 1000)); // 15 min
          document.cookie = `languageBeforeRedirect=${this.activeLanguage}; expires=${date.toUTCString()}`;
     }

     public restoreBackupLanguage() {
          var lang = this.getCookie('languageBeforeRedirect'); 
          if (lang) {
               this.setActiveLanguage(lang);
          }
     }

     public observeActiveLanguage: Observable<string> = new Observable<string>(observer => {
          return observer.next(this.activeLanguage);
     });

     public get getLanguagesRouteCanSee():string[]{
          // if the current route is not in the list of routes that have languages, then it's the wild card route. Only return EN.
          let baseRoute = this.currentRoute;
          if (this.currentRoute.indexOf('?') > 0) {
               baseRoute = this.currentRoute.substring(0, this.currentRoute.indexOf('?'));
          }
          
          if (!this.langsPerPath.some(v => v.path === baseRoute)) {
               return ['EN'];
          }
          const pathLanguages = this.langsPerPath.find(v => v.path === baseRoute) || null;
          if(pathLanguages !== null){
               return pathLanguages.languages;
          };    
          return ['EN','ES','FR'];
     }

     public getTextForByTag(tag:string,file:string = 'base'){
          let baseRoute = this.currentRoute;
          if (this.currentRoute.indexOf('?') > 0) {
               baseRoute = this.currentRoute.substring(0, this.currentRoute.indexOf('?'));
          }
          
          const _row = this.translations.find(f => f.file === file)?.translations.find(t => t.tag === tag) || null;
          
          const pathLanguages = this.langsPerPath.find(v => v.path === baseRoute) || null;
          if(_row !== null){
               if(!(pathLanguages?.languages.includes(this.activeLanguage.toUpperCase())) || this.activeLanguage === "en"){
                    return _row.en;
               }else if(this.activeLanguage == "es"){
                    return _row.es;
               }else if(this.activeLanguage == "fr"){
                    return _row.fr;
               }else{
                    return _row.en;
               }
          }
          return '';
     }

     translateDate(value: string, language: string) {
          language = language.toUpperCase();
          if (value && value.length > 2) {
              if (language === 'ES') {
                  if (value.includes('January')) {
                      value = value.replace('January', 'enero');
                  } else if (value.includes('February')) {
                      value = value.replace('February', 'febrero');
                  } else if (value.includes('March')) {
                      value = value.replace('March', 'marzo');
                  } else if (value.includes('April')) {
                      value = value.replace('April', 'abril');
                  } else if (value.includes('May')) {
                      value = value.replace('May', 'mayo');
                  } else if (value.includes('June')) {
                      value = value.replace('June', 'junio');
                  } else if (value.includes('July')) {
                      value = value.replace('July', 'julio');
                  } else if (value.includes('August')) {
                      value = value.replace('August', 'agosto');
                  } else if (value.includes('September')) {
                      value = value.replace('September', 'septiembre');
                  } else if (value.includes('October')) {
                      value.replace('October', 'octubre');
                  } else if (value.includes('November')) {
                      value = value.replace('November', 'noviembre');
                  } else {
                      value = value.replace('December', 'diciembre');
                  }
              } else if (language === 'FR') {
                  value = 'Need French Translations for dates';
              }
          }
          return value;
     }
     
     getCookie(name: string): string | undefined {
          const value = `; ${document.cookie}`;
          const parts = value.split(`; ${name}=`);
          if (parts && parts.length === 2) {
               const val = parts.pop();
               if (val) {
                    return val.split(';').shift();
               } else {
                    console.error('Cookie null for ' + name);
                    return;
               }
          }
          return '';
     }
}
